import type { SvgIconComponent } from '@mui/icons-material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import WidgetsIcon from '@mui/icons-material/Widgets';
import ListAltIcon from '@mui/icons-material/ListAlt';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import HistoryIcon from '@mui/icons-material/History';
import SettingsIcon from '@mui/icons-material/Settings';
import type { TabKey } from '../stores/store';

export interface RoleFlags {
	isAdmin: boolean;
	isYard: boolean;
	isPacker: boolean;
	isPM: boolean;
	isDelivery: boolean;
}

export interface NavItem {
	key: TabKey;
	path: string;
	label: string;
	shortLabel: string;
	icon: SvgIconComponent;
	show: (r: RoleFlags) => boolean;
	/** Shown directly in the mobile bottom bar; others go under "More". */
	mobilePrimary: boolean;
}

const ALL: NavItem[] = [
	{ key: 'scan', path: '/scan', label: 'Scan & Update', shortLabel: 'Scan', icon: QrCodeScannerIcon, show: (r) => r.isYard || r.isAdmin, mobilePrimary: true },
	{ key: 'inventory', path: '/inventory', label: 'Inventory', shortLabel: 'Inventory', icon: Inventory2Icon, show: () => true, mobilePrimary: true },
	{ key: 'pallets', path: '/pallets', label: 'Pallets', shortLabel: 'Pallets', icon: WidgetsIcon, show: (r) => r.isYard || r.isDelivery || r.isAdmin, mobilePrimary: true },
	{ key: 'packing', path: '/packing', label: 'Packing Lists', shortLabel: 'Lists', icon: ListAltIcon, show: (r) => r.isPacker || r.isDelivery || r.isAdmin, mobilePrimary: true },
	{ key: 'requests', path: '/requests', label: 'Packing Requests', shortLabel: 'Requests', icon: AssignmentTurnedInIcon, show: (r) => r.isPM || r.isPacker || r.isAdmin, mobilePrimary: true },
	{ key: 'projects', path: '/projects', label: 'Projects', shortLabel: 'Projects', icon: FolderOpenIcon, show: () => true, mobilePrimary: false },
	{ key: 'activity', path: '/activity', label: 'Activity', shortLabel: 'Activity', icon: HistoryIcon, show: (r) => r.isAdmin, mobilePrimary: false },
	{ key: 'config', path: '/config', label: 'Locations & Users', shortLabel: 'Admin', icon: SettingsIcon, show: (r) => r.isAdmin, mobilePrimary: false },
];

export function visibleNavItems(roles: RoleFlags): NavItem[] {
	return ALL.filter((i) => i.show(roles));
}

/** The screen each role lands on by default (mirrors the old initialTab logic). */
export function defaultPath(roles: RoleFlags): string {
	if (roles.isYard) return '/scan';
	if (roles.isPacker) return '/packing';
	if (roles.isDelivery) return '/packing';
	if (roles.isPM) return '/requests';
	return '/inventory';
}

/**
 * Split visible items for the mobile bottom bar: at most `maxPrimary` slots get
 * direct actions; the remainder (plus any non-primary) go under a "More" sheet.
 * The last slot is reserved for the More action when there is overflow.
 */
export function splitForBottomNav(items: NavItem[], maxPrimary = 4): { primary: NavItem[]; overflow: NavItem[] } {
	const primaryCandidates = items.filter((i) => i.mobilePrimary);
	const rest = items.filter((i) => !i.mobilePrimary);

	if (items.length <= maxPrimary) {
		return { primary: items, overflow: [] };
	}
	// Reserve one slot for "More".
	const primary = primaryCandidates.slice(0, maxPrimary - 1);
	const overflow = [...primaryCandidates.slice(maxPrimary - 1), ...rest];
	return { primary, overflow };
}
